/**
 * AudioManager - Core Web Audio API wrapper
 *
 * Manages the AudioContext, handles iOS unlock pattern,
 * and provides a clean API for the rest of the game.
 *
 * Key principles from CLAUDE.md:
 * - Use Web Audio API (not <audio> tags)
 * - iOS audio must be unlocked via user gesture
 * - Audio clarity and timing matter more than visuals
 */

export class AudioManager {
  private context: AudioContext | null = null;
  private isUnlocked = false;
  private unlockListeners: Array<() => void> = [];

  // Master gain nodes for mixing
  private masterGain: GainNode | null = null;
  private speechGain: GainNode | null = null;
  private backgroundGain: GainNode | null = null;

  constructor() {
    // Don't create AudioContext yet - wait for user gesture on iOS
  }

  /**
   * Initialize audio context on first user interaction
   * This MUST be called from a user gesture handler (touch/click)
   */
  async unlock(): Promise<boolean> {
    if (this.isUnlocked) {
      return true;
    }

    try {
      // Create AudioContext
      this.context = new (window.AudioContext || (window as any).webkitAudioContext)();

      // Create master gain chain
      this.masterGain = this.context.createGain();
      this.masterGain.connect(this.context.destination);

      // Speech channel (for phonemes, blends, words)
      this.speechGain = this.context.createGain();
      this.speechGain.connect(this.masterGain);

      // Background channel (for ambient sounds)
      this.backgroundGain = this.context.createGain();
      this.backgroundGain.connect(this.masterGain);

      // iOS requires playing a silent sound to unlock
      if (this.context.state === 'suspended') {
        await this.context.resume();
      }

      // Play a silent buffer to truly unlock iOS audio
      const buffer = this.context.createBuffer(1, 1, 22050);
      const source = this.context.createBufferSource();
      source.buffer = buffer;
      source.connect(this.context.destination);
      source.start(0);

      this.isUnlocked = true;

      // Notify listeners
      this.unlockListeners.forEach(listener => listener());
      this.unlockListeners = [];

      console.log('✓ Audio unlocked');
      return true;
    } catch (err) {
      console.error('Failed to unlock audio:', err);
      return false;
    }
  }

  /**
   * Register callback for when audio is unlocked
   */
  onUnlock(callback: () => void): void {
    if (this.isUnlocked) {
      callback();
    } else {
      this.unlockListeners.push(callback);
    }
  }

  /**
   * Get the audio context (only available after unlock)
   */
  getContext(): AudioContext | null {
    return this.context;
  }

  /**
   * Get speech gain node (for phonemes, words, blending)
   */
  getSpeechGain(): GainNode | null {
    return this.speechGain;
  }

  /**
   * Get background gain node (for ambient sounds)
   */
  getBackgroundGain(): GainNode | null {
    return this.backgroundGain;
  }

  /**
   * Check if audio is ready to use
   */
  isReady(): boolean {
    return this.isUnlocked && this.context !== null;
  }

  /**
   * Duck background audio when speech plays
   * (Reduce background volume to make speech clearer)
   */
  duckBackground(duration: number = 0.1): void {
    if (!this.backgroundGain || !this.context) return;

    const now = this.context.currentTime;
    this.backgroundGain.gain.cancelScheduledValues(now);
    this.backgroundGain.gain.setValueAtTime(this.backgroundGain.gain.value, now);
    this.backgroundGain.gain.linearRampToValueAtTime(0.2, now + duration);
  }

  /**
   * Restore background audio volume after speech
   */
  unduckBackground(duration: number = 0.3): void {
    if (!this.backgroundGain || !this.context) return;

    const now = this.context.currentTime;
    this.backgroundGain.gain.cancelScheduledValues(now);
    this.backgroundGain.gain.setValueAtTime(this.backgroundGain.gain.value, now);
    this.backgroundGain.gain.linearRampToValueAtTime(1.0, now + duration);
  }

  /**
   * Set master volume
   */
  setMasterVolume(volume: number): void {
    if (!this.masterGain) return;
    this.masterGain.gain.value = Math.max(0, Math.min(1, volume));
  }

  /**
   * Cleanup
   */
  async dispose(): Promise<void> {
    if (this.context) {
      await this.context.close();
      this.context = null;
      this.isUnlocked = false;
    }
  }
}

// Singleton instance
let audioManager: AudioManager | null = null;

export function getAudioManager(): AudioManager {
  if (!audioManager) {
    audioManager = new AudioManager();
  }
  return audioManager;
}
